/**
 * The tenant setting that decides whether a programme lead may manage their
 * own team.
 *
 * One reader, because there were two: the route asks it to decide which
 * programmes it may offer at all, and the panel asked it again to decide the
 * same thing, so one team screen read the tenant settings twice and each copy
 * carried its own fallback.
 *
 * @module components/teams/useLeadTeamManagement
 */

import { useEffect, useState } from "react";

import type { ProgrammePlatformClient } from "@/platform/client";

/**
 * @param client - The platform client to read the setting through.
 * @param known - The answer a caller already has, which skips the read.
 * @returns Whether a lead may manage their own team. Permissive until the
 *   read answers, and permissive if it fails: the writes are refused server
 *   side either way, and hiding them from a lead who holds the right is worse
 *   than offering one that is refused.
 */
export function useLeadTeamManagement(
  client: ProgrammePlatformClient,
  known?: boolean | undefined,
): boolean {
  const [allowed, setAllowed] = useState(true);
  useEffect(() => {
    if (known !== undefined) {
      return undefined;
    }
    let active = true;
    client
      .getTenantProviderSettings()
      .then((record) => {
        if (active) {
          setAllowed(record.settings.allowProgrammeLeadTeamManagement !== false);
        }
      })
      .catch(() => {
        if (active) {
          setAllowed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [client, known]);
  return known ?? allowed;
}
